import { AssessmentFormGroup } from '@apps/materiality-tracker/components/assessment-create/AssessmentFormGroup';
import { AssessmentForm } from '@apps/materiality-tracker/hooks/useCreateAssessmentForm';
import { DashboardDivider } from '@g17eco/atoms/divider';
import { SelectFactory, SelectTypes, type Option } from '@g17eco/molecules/select/SelectFactory';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { AssessmentType } from '@g17eco/types/survey';
import { UniversalTrackerBase } from '@g17eco/types/universalTracker';
import { useToggle } from '@hooks/useToggle';
import { getMonthNonJSOptions, getYearDropdownOptions } from '@utils/date';
import type { FormatOptionLabelMeta } from 'react-select';
import { Button, FormGroup, Label, Modal, ModalBody, ModalHeader } from 'reactstrap';

const YEARS_BACK = 6;
const YEARS_NEXT = 2;

const VERIFICATION_REQUIRED_TOOLTIP =
  'Two-step verification is an optional feature which you can enable for your assessments. It\'s great for situations with multiple contributors or when you want an extra review of answers before finalising results.';

const yearOptions = getYearDropdownOptions(YEARS_BACK, YEARS_NEXT);

const monthOptions = getMonthNonJSOptions(); // 1-12

const assessmentTypeOptions = [
  {
    label: (
      <>
        <i className='fal fa-buildings mr-2' />
        Financial materiality assessment
      </>
    ),
    value: AssessmentType.FinancialMateriality,
  },
  {
    label: (
      <>
        <i className='fal fa-earth-europe mr-2' />
        Double materiality assessment
      </>
    ),
    value: AssessmentType.DoubleMateriality,
  },
];

const verificationOptions = [
  {
    title: 'Verification not required',
    subtitle:
      'Verification is not required before submitting your assessment. Verification can be switched on for individual metrics.',
    value: false,
  },
  {
    title: 'Verification required',
    subtitle: 'All questions must be separately verified before submitting your assessment and generating results.',
    value: true,
  },
];

const verificationSelectOptions = verificationOptions.map(({ title, subtitle, value }) => ({
  label: (
    <div className='p-1'>
      <b>{title}</b>
      <div>{subtitle}</div>
    </div>
  ),
  value,
}));

const formatOptionLabel = (option: Option<boolean> | null, meta: FormatOptionLabelMeta<Option<boolean> | null>) => {
  if (!option) {
    return <></>;
  }
  const selectedOption = verificationOptions.find((i) => i.value === option.value);
  if (meta.context === 'value' && selectedOption) {
    return selectedOption.title;
  }
  return option.label;
};

interface Props extends Pick<AssessmentForm, 'verificationRequired' | 'assessmentType'> {
  updateAssessmentForm: (field: keyof AssessmentForm, value: AssessmentForm[keyof AssessmentForm]) => void;
  handleOptionChange: (key: string, value?: string) => void;
  utrs: UniversalTrackerBase[];
}

export const AssessmentConfigurationForm = (props: Props) => {
  const { verificationRequired, assessmentType, utrs, updateAssessmentForm, handleOptionChange } = props;

  return (
    <div className='text-ThemeTextMedium'>
      <h5 className='text-ThemeHeadingDark mb-3'>Settings</h5>
      <FormGroup>
        <Label htmlFor='assessment-year' className='fw-semibold'>
          Assessment date
        </Label>
        <div className='d-flex gap-3'>
          <SelectFactory
            className='w-100'
            selectType={SelectTypes.SingleSelect}
            options={monthOptions}
            placeholder='Select month'
            onChange={(op) => (op ? updateAssessmentForm('month', op.value) : undefined)}
          />
          <SelectFactory
            className='w-100'
            selectType={SelectTypes.SingleSelect}
            options={yearOptions}
            placeholder='Select year'
            onChange={(op) => (op ? updateAssessmentForm('year', op.value) : undefined)}
          />
        </div>
      </FormGroup>
      <FormGroup className='mt-3'>
        <Label htmlFor='assessment-type' className='fw-semibold d-flex align-items-center justify-content-between'>
          Assessment type
          <InfoButton />
        </Label>
        <SelectFactory
          selectType={SelectTypes.SingleSelect}
          options={assessmentTypeOptions}
          placeholder='Select assessment type'
          onChange={(op) => (op ? updateAssessmentForm('assessmentType', op.value) : undefined)}
          value={assessmentTypeOptions.find((op) => op.value === assessmentType)}
        />
      </FormGroup>
      <FormGroup className='mt-3'>
        <Label
          htmlFor='verification-required'
          className='fw-semibold d-flex align-items-center justify-content-between'
        >
          Verification requirements
          <SimpleTooltip text={VERIFICATION_REQUIRED_TOOLTIP}>
            <i className='fal text-lg fa-info-circle text-ThemeIconSecondary' />
          </SimpleTooltip>
        </Label>
        <SelectFactory
          selectType={SelectTypes.SingleSelect}
          options={verificationSelectOptions}
          formatOptionLabel={formatOptionLabel}
          onChange={(op) => (op ? updateAssessmentForm('verificationRequired', op.value) : undefined)}
          value={verificationSelectOptions.find((op) => op.value === verificationRequired)}
        />
      </FormGroup>
      <DashboardDivider className='my-4 border-ThemeBorderDefault' />
      <h5 className='text-ThemeHeadingDark mb-3'>Configuration</h5>
      {utrs.map((utr) => (
        <AssessmentFormGroup key={utr.code} utr={utr} onChange={handleOptionChange} tooltip={utr.instructions} />
      ))}
    </div>
  );
};

const InfoButton = () => {
  const [infoModal, toggleModal, setInfoModal] = useToggle();

  return (
    <>
      <Button color='link' onClick={() => setInfoModal(true)}>
        <i className='fal fa-circle-question mr-2' />
        Which is right for me
      </Button>
      <Modal isOpen={infoModal} toggle={toggleModal} backdrop='static'>
        <ModalHeader toggle={toggleModal}>Which is right for me?</ModalHeader>
        <ModalBody>
          <strong>Financial Materiality Assessment</strong>
          <p>
            Financial only assessments focus on how sustainability issues (like climate change, diversity, or resource
            use) could impact your company's financial performance. It helps you understand which topics could affect
            your profits, costs, or overall business value — often used for investor-focused reporting.
          </p>
          <strong>Double Materiality Assessment</strong>
          <p>
            Double materiality assessments take things a step further. It looks at both how sustainability issues affect
            the <strong>financial</strong> health of your company and how your company <strong>impacts</strong> people
            and the environment. This approach gives a more complete view and is required under frameworks like the EU's
            Corporate Sustainability Reporting Directive (CSRD).
          </p>
        </ModalBody>
      </Modal>
    </>
  );
};
